import { useState, useEffect } from 'react';
import get from 'lodash/get';
import useLowest from './useLowest';
import { getIndexBasedSkip, getNextAndPrevIds, getResult } from './utils';

const updateQuery = (append) => (prev, { fetchMoreResult }) => {
  if (!fetchMoreResult) return prev;

  const [entityKey] = Object.keys(fetchMoreResult);

  const data = append
    ? [...prev[entityKey].data, ...fetchMoreResult[entityKey].data]
    : [...fetchMoreResult[entityKey].data, ...prev[entityKey].data];

  return {
    ...prev,
    [entityKey]: {
      ...prev[entityKey],
      data,
    },
  };
};

export default function useIdsHidrator({
  id,
  params,
  fetchMore,
  variables,
  data,
}) {
  const [skip, setSkip] = useState(() => parseInt(params.skip || 0, 10));
  const lowestSkip = useLowest(skip);

  const result = getResult(data);
  const { prevId, nextId, index } = getNextAndPrevIds(id, result);

  // When you start with a skip higher than 0, the index of the entities
  // will be relative to the loaded data
  // This absolute index, makes sure a entity will always get the index
  // based all the whole search result.

  const absoluteIndex = lowestSkip + index;
  const { limit } = variables;
  const total = get(result, 'meta.total');
  const totalLoaded = get(result, 'data.length');

  useEffect(() => {
    if (index === -1) {
      return;
    }

    // when going backwards and forwards, sometimes we are going to skip a fetchMore,
    // because we already have results to that relative index
    // Because of that, the skip params can get outdated.
    // The index based skip will get always the right skip for fetching new results
    const indexBasedSkip = getIndexBasedSkip(absoluteIndex, variables.limit);

    if (index === 0 && skip > 0) {
      const newSkip = Math.max(indexBasedSkip - limit, 0);
      setSkip(newSkip);
      fetchMore({
        variables: {
          skip: newSkip,
        },
        updateQuery: updateQuery(false),
      });
    }

    const lastIndex = totalLoaded - 1;

    if (index === lastIndex && skip + limit < total) {
      const newSkip = indexBasedSkip + limit;
      setSkip(newSkip);
      fetchMore({
        variables: {
          skip: newSkip,
        },
        updateQuery: updateQuery(true),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index]);

  return { prevId, nextId, index: absoluteIndex };
}
